// 세션 녹화(설계서 §9.7) — 순수 로직만 둔다(브라우저 API 없음 → Node 에서 그대로 시험한다).
//
// 녹화 자체의 메시지 규약은 `ctrl` 채널에 있다(`quality/protocol.ts` 의 rec/rok/rerr). 여기 있는 것은
// **파일 이름·이벤트 기록·상한** 처럼 규약과 무관하게 고정해 두고 싶은 값들이다.

/** 녹화 조각이 메모리에 쌓이는 한계(§9.7.4). 넘으면 스스로 멈추고 저장한다. */
export const MAX_RECORDING_BYTES = 1024 * 1024 * 1024; // 1 GiB

/**
 * 녹화 컨테이너·코덱 후보. 받은 스트림을 **재인코딩하지 않는 것**이 목적이라 VP8 을 먼저 고른다
 * (에이전트가 보내는 코덱 — §10). 브라우저가 못 받으면 뒤엣것으로 내려간다.
 */
export const RECORDING_MIME_CANDIDATES: readonly string[] = [
  'video/webm;codecs=vp8',
  'video/webm',
];

/** 녹화 파일 확장자(위 후보가 전부 webm 이다). */
export const RECORDING_EXT = 'webm';

/** 이벤트 기록에 남기는 사건 종류(§9.7.5). */
export type RecordingEventKind = 'file' | 'clipboard' | 'chat' | 'quality' | 'control' | 'session' | 'draw';

export interface RecordingEvent {
  /** 녹화 시작 기준 **초**(벽시계가 아니다 — 영상과 맞춰 보는 것이 목적이다). */
  t: number;
  kind: RecordingEventKind;
  /** 🔴 무슨 일이 있었는지만. 채팅 본문·클립보드 값·파일 내용은 넣지 않는다(§9.7.5). */
  text: string;
}

export interface RecordingTimeline {
  startedAt: string;
  /** 녹화한 기기(표시명 또는 접속 코드). */
  device: string;
  /** 영상 길이(초, 소수 1자리). */
  durationSec: number;
  events: RecordingEvent[];
}

/** 파일 이름에 쓸 시각 도장 — `20260923-142530`. */
export function stampFor(d: Date): string {
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return (
    `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}` +
    `-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`
  );
}

/**
 * 저장 파일 이름. 기기 이름이 파일 이름에 쓸 수 없는 글자를 담을 수 있어(사람이 정하는 값이다) 걸러 낸다.
 * 걸러 낸 결과가 비면 `세션` 으로 둔다 — 이름 없는 파일을 만들지 않는다.
 */
export function recordingFileName(device: string, at: Date, ext = RECORDING_EXT, prefix = '원격지원'): string {
  const safe = device.replace(/[\\/:*?"<>|]/g, '').trim() || '세션';
  return `${prefix}-${safe}-${stampFor(at)}.${ext}`;
}

/** 이벤트 기록(JSON) 직렬화 — 영상과 같은 이름에 확장자만 다르다. */
export function timelineJson(timeline: RecordingTimeline): string {
  return JSON.stringify(timeline, null, 2);
}

/** 이 브라우저가 쓸 수 있는 첫 후보(없으면 null → 녹화 단추를 숨긴다). */
export function pickRecordingMime(
  isSupported: (mime: string) => boolean,
  candidates: readonly string[] = RECORDING_MIME_CANDIDATES,
): string | null {
  return candidates.find((m) => isSupported(m)) ?? null;
}

/** 바이트 수를 사람이 읽는 크기로(안내 문구용). */
export function humanBytes(n: number): string {
  if (n < 1024) {
    return `${n}B`;
  }
  const units = ['KB', 'MB', 'GB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 ? 0 : 1)}${units[i]}`;
}

/** 경과 초를 `12:34` 로(녹화 단추 옆 표시용). */
export function elapsedLabel(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}
